import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsuarioLogado, getUsuarioSistemaId, apiCallAuth } from '../utils/auth';
import './AlterarSenha.css';
import BASE_URL from '../utils/api';

export default function AlterarSenha() {
  const navigate = useNavigate();
  const [oldSenha, setOldSenha] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmSenha, setConfirmSenha] = useState('');
  const [showOld, setShowOld] = useState(false);
  const [showNova, setShowNova] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  // Estados para validação em tempo real
  const [hasMinLength, setHasMinLength] = useState(false);
  const [hasLetterNumberSpecial, setHasLetterNumberSpecial] = useState(false);

  useEffect(() => {
    const user = getUsuarioLogado();
    if (!user) navigate('/sidebar/Login');
  }, [navigate]);

  useEffect(() => {
    // Validação em tempo real da nova senha
    if (!novaSenha) {
      setHasMinLength(false);
      setHasLetterNumberSpecial(false);
      return;
    }
    setHasMinLength(novaSenha.length >= 6);
    const re = /(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])/;
    setHasLetterNumberSpecial(re.test(novaSenha));
  }, [novaSenha]);

  const senhaValida = (s) => {
    if (!s) return false;
    if (s.length < 6) return false;
    // must contain letter, number and special char
    const re = /(?=.*[A-Za-z])(?=.*\d)(?=.*[^A-Za-z0-9])/;
    return re.test(s);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!oldSenha) return setError('Informe a senha antiga.');
    if (!novaSenha || !confirmSenha) return setError('Preencha nova senha e confirmação.');
    if (novaSenha !== confirmSenha) return setError('Nova senha e confirmação não conferem.');
    if (!senhaValida(novaSenha)) return setError('Senha inválida: mínimo 6 caracteres e conter letras, números e caractere especial.');

    setLoading(true);
    try {
      const user = getUsuarioLogado();
      if (!user || !user.login) throw new Error('Usuário não identificado.');

      // Verificar senha antiga via endpoint de login
      const loginRes = await fetch(`${BASE_URL}/Auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login: user.login, senha: oldSenha })
      });

      if (!loginRes.ok) {
        const txt = await loginRes.text();
        throw new Error(txt || 'Senha antiga incorreta.');
      }

      // Alterar senha APENAS no banco CENTRAL (não toca na instância)
      const usuarioSistemaId = getUsuarioSistemaId();
      if (!usuarioSistemaId) throw new Error('ID do usuário não encontrado.');

      await apiCallAuth(`UsuarioSistema/${usuarioSistemaId}/senha`, 'PATCH', {
        NovaSenha: novaSenha
      });

      setSuccess('Senha alterada com sucesso.');
      setOldSenha('');
      setNovaSenha('');
      setConfirmSenha('');

    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="alterar-senha-container" style={{ position: 'relative' }}>
      <button
        onClick={() => navigate('/sidebar/Configuracoes')}
        className="planos-btn-voltar"
        style={{ position: 'absolute', top: 10, left: 10, zIndex: 20 }}
        title="Voltar para Configurações"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
          <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
        Voltar
      </button>
      <div className="alterar-senha-card">
        <h2>Alterar Senha</h2>
        <p>Informe sua senha atual e escolha uma nova senha.</p>

        {error && <div className="alert error">{error}</div>}
        {success && <div className="alert success">{success}</div>}

        <form className="alterar-senha-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Senha antiga</label>
            <div className="input-with-icon">
              <input type={showOld ? 'text' : 'password'} value={oldSenha} onChange={(e) => setOldSenha(e.target.value)} required />
              <button type="button" className="password-toggle" onClick={() => setShowOld(s => !s)} aria-label="Mostrar senha">
                {showOld ? (
                  /* closed-eye when password is visible */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9-7-9-7a18.3 18.3 0 0 1 3.08-3.94" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M1 1l22 22" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  /* open-eye when password is hidden */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.5 12a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>Nova senha</label>
            <div className="input-with-icon">
              <input type={showNova ? 'text' : 'password'} value={novaSenha} onChange={(e) => setNovaSenha(e.target.value)} required />
              <button type="button" className="password-toggle" onClick={() => setShowNova(s => !s)} aria-label="Mostrar senha">
                {showNova ? (
                  /* closed-eye when password is visible */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9-7-9-7a18.3 18.3 0 0 1 3.08-3.94" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M1 1l22 22" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  /* open-eye when password is hidden */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.5 12a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </button>
            </div>
            <div className="password-rules">
              <div className={hasMinLength ? 'rule-valid' : 'rule-invalid'}>• Mínimo 6 caracteres</div>
              <div className={hasLetterNumberSpecial ? 'rule-valid' : 'rule-invalid'}>• Deve conter letras, números e caractere especial</div>
            </div>
          </div>

          <div className="form-group">
            <label>Confirmar nova senha</label>
            <div className="input-with-icon">
              <input type={showConfirm ? 'text' : 'password'} value={confirmSenha} onChange={(e) => setConfirmSenha(e.target.value)} required />
              <button type="button" className="password-toggle" onClick={() => setShowConfirm(s => !s)} aria-label="Mostrar senha">
                {showConfirm ? (
                  /* closed-eye when password is visible */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-5 0-9-7-9-7a18.3 18.3 0 0 1 3.08-3.94" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M1 1l22 22" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                ) : (
                  /* open-eye when password is hidden */
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 12s4-7 9-7 9 7 9 7-4 7-9 7-9-7-9-7z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/><path d="M9.5 12a2.5 2.5 0 1 0 5 0 2.5 2.5 0 0 0-5 0z" stroke="#666" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                )}
              </button>
            </div>
          </div>

          <button type="submit" className="btn-alterar" disabled={loading}>{loading ? 'Alterando...' : 'Alterar Senha'}</button>
        </form>
      </div>
    </div>
  );
}
